import { applyLiquidFarmFilter, applyLockedFarmFilterByDuration } from "@/components/vote/utils";
import { useGlobalVotesStore, useProtocolStore, useUserVotesStore } from "../stores";
import type { Farm } from "@/services/real/protocol.service";
import type { Vote } from "@/types";
import { convertVotesToPercentages } from "@/utils/vote";
import { useState, useCallback, useEffect } from "react";
import { normalizeTo100 } from "@/utils/vote";
import type { VotePercentage } from "@/services/real/votes.service";
import { type LockedPositionStore, useLockPositions } from "@/hooks/stores/useLockPositions";

/**
 * Adjusts the vote percentages so that their sum is 100%
 * Used to ensure that the vote percentages are always 100% even if buckets are trimmed
 * @param votes - The votes to adjust
 * @returns The adjusted votes
 */
function adjustReducedVotePercentages(votes: Vote[]) {
    const total = votes.reduce((acc, vote) => acc + Number(vote.percentage), 0);

    return votes.map((vote) => {
        return {
            ...vote,
            percentage: total > 0 ? (Number(vote.percentage) / total) * 100 : 0,
        };
    });
}

/**
 * Gets the maximum locked position bucket
 * @param lockedPositions - The locked positions
 * @returns The maximum locked position bucket
 */
function getMaxLockedPositionBucket(lockedPositions: LockedPositionStore[]) {
    if (lockedPositions.length === 0) {
        return 0;
    }

    const reversed = lockedPositions.sort(
        (a, b) => Number(b.lockDuration.split("w")[0]) - Number(a.lockDuration.split("w")[0]),
    );

    const index = reversed.findIndex((position) => Number(position.liUSDBalance) > 0);

    if (index === -1) {
        // Return all buckets so liquid users can see how voting will look like
        return Number(reversed[0].lockDuration.split("w")[0]);
    }

    return Number(reversed[index].lockDuration.split("w")[0]);
}

export function useMaxLockedPositionBucket() {
    const { lockedPositions, totalLocked } = useLockPositions();
    return { maxDuration: getMaxLockedPositionBucket(lockedPositions), totalLocked };
}

export function useLiquidFarms() {
    const { farms } = useProtocolStore();
    return applyLiquidFarmFilter(farms);
}

export function useIlliquidFarms(lockedPositions: LockedPositionStore[]) {
    const { farms } = useProtocolStore();
    const maxDuration = getMaxLockedPositionBucket(lockedPositions);
    const filteredFarms = applyLockedFarmFilterByDuration(farms, maxDuration);
    return { farms: filteredFarms, maxDuration };
}

// get global votes for the farms cast by the protocol users for the next and current epoch
export function useGlobalVotes(farms: Farm[]) {
    const { nextEpochVotes, currentEpochVotes } = useGlobalVotesStore();

    const allNextEpochVotes = [...(nextEpochVotes?.illiquid.votes ?? []), ...(nextEpochVotes?.liquid.votes ?? [])];
    const allCurrentEpochVotes = [
        ...(currentEpochVotes?.illiquid.votes ?? []),
        ...(currentEpochVotes?.liquid.votes ?? []),
    ];

    const nextEpochVotesFiltered = allNextEpochVotes.filter((vote) =>
        farms.find((farm) => farm.address.toLowerCase() === vote.farmAddress.toLowerCase()),
    );
    const currentEpochVotesFiltered = allCurrentEpochVotes.filter((vote) =>
        farms.find((farm) => farm.address.toLowerCase() === vote.farmAddress.toLowerCase()),
    );

    // Tune vote percentages to ensure they sum to 100%
    const nextEpochVotesFilteredAdjusted = adjustReducedVotePercentages(nextEpochVotesFiltered);
    const currentEpochVotesFilteredAdjusted = adjustReducedVotePercentages(currentEpochVotesFiltered);

    if (nextEpochVotesFilteredAdjusted.length !== farms.length) {
        const missingFarms = farms.filter(
            (farm) =>
                !nextEpochVotesFilteredAdjusted.find(
                    (vote) => vote.farmAddress.toLowerCase() === farm.address.toLowerCase(),
                ),
        );

        for (const farm of missingFarms) {
            nextEpochVotesFilteredAdjusted.push({
                farmAddress: farm.address,
                percentage: 0,
                votesCast: "0",
            });
        }
    }

    if (currentEpochVotesFilteredAdjusted.length !== farms.length) {
        const missingFarms = farms.filter(
            (farm) =>
                !currentEpochVotesFilteredAdjusted.find(
                    (vote) => vote.farmAddress.toLowerCase() === farm.address.toLowerCase(),
                ),
        );

        for (const farm of missingFarms) {
            currentEpochVotesFilteredAdjusted.push({
                farmAddress: farm.address,
                percentage: 0,
                votesCast: "0",
            });
        }
    }

    return {
        nextEpochVotes: nextEpochVotesFilteredAdjusted,
        currentEpochVotes: currentEpochVotesFilteredAdjusted,
    };
}

export function useUserVotes(farms: Farm[]) {
    const { nextEpochVotes, currentEpochVotes } = useUserVotesStore();

    const allNextEpochVotes = [...(nextEpochVotes?.illiquid.votes ?? []), ...(nextEpochVotes?.liquid.votes ?? [])];
    const allCurrentEpochVotes = [
        ...(currentEpochVotes?.illiquid.votes ?? []),
        ...(currentEpochVotes?.liquid.votes ?? []),
    ];

    const nextEpochVotesFiltered = allNextEpochVotes.filter((vote) =>
        farms.find((farm) => farm.address.toLowerCase() === vote.farmAddress.toLowerCase()),
    );
    const currentEpochVotesFiltered = allCurrentEpochVotes.filter((vote) =>
        farms.find((farm) => farm.address.toLowerCase() === vote.farmAddress.toLowerCase()),
    );

    // Tune vote percentages to ensure they sum to 100%
    const nextEpochVotesFilteredAdjusted = adjustReducedVotePercentages(nextEpochVotesFiltered);
    const currentEpochVotesFilteredAdjusted = adjustReducedVotePercentages(currentEpochVotesFiltered);

    return {
        nextUserEpochVotes: nextEpochVotesFilteredAdjusted,
        currentUserEpochVotes: currentEpochVotesFilteredAdjusted,
    };
}

export function useSelectedWeights() {
    const [selectedWeights, setSelectedWeights] = useState<number[]>([]);

    const handleVoteChange = (index: number, value: number) => {
        setSelectedWeights((prev) => {
            const newWeights = [...prev];
            newWeights[index] = value;
            return normalizeTo100(newWeights, index);
        });
    };

    const setVotesFromPercentages = (newVotes: VotePercentage[]) => {
        const percentages = convertVotesToPercentages(newVotes);
        const normalizedValues = normalizeTo100(percentages, 0);
        setSelectedWeights(normalizedValues);
    };

    return { selectedWeights, handleVoteChange, setVotesFromPercentages };
}

export function useControlledVoting(farms: Farm[]) {
    const { selectedWeights, handleVoteChange, setVotesFromPercentages } = useSelectedWeights();

    const { nextEpochVotes, currentEpochVotes } = useGlobalVotes(farms);
    const { currentUserEpochVotes } = useUserVotes(farms);

    const nextEpochPercentages = nextEpochVotes.map((vote) => Number(vote.percentage.toFixed(2)));
    const currentEpochPercentages = currentEpochVotes.map((vote) => Number(vote.percentage.toFixed(2)));

    const handleCopyPrevious = useCallback(() => {
        if (!currentUserEpochVotes || currentUserEpochVotes.length === 0) return;
        setVotesFromPercentages(currentUserEpochVotes);
    }, [currentUserEpochVotes, setVotesFromPercentages]);

    const handleMatchDistribution = useCallback(() => {
        if (!currentEpochVotes || currentEpochVotes.length === 0) return;
        setVotesFromPercentages(currentEpochVotes);
    }, [currentEpochVotes, setVotesFromPercentages]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (selectedWeights.length === 0) {
            // Only update if not already initialized
            if (nextEpochPercentages.length > 0) {
                setVotesFromPercentages(nextEpochVotes);
            } else if (currentEpochPercentages.length > 0) {
                setVotesFromPercentages(currentEpochVotes);
            }
        }
    }, [nextEpochPercentages, currentEpochPercentages]);

    return {
        selectedWeights,
        nextEpochVotes,
        currentEpochVotes,
        handleCopyPrevious,
        handleMatchDistribution,
        handleVoteChange,
    };
}

export function useVoteValidation() {
    const { farms } = useProtocolStore();
    const { maxDuration: unwindingEpochs, totalLocked } = useMaxLockedPositionBucket();
    const { nextUserEpochVotes } = useUserVotes(farms);

    const noLockedPositions = Number(totalLocked) === 0;
    const alreadyVoted = nextUserEpochVotes.length > 0;

    return { noLockedPositions, alreadyVoted, unwindingEpochs };
}
